import * as React from 'react';

import { dismissToast } from '@/hooks/use-toast';

import type { ToasterToast } from '@/hooks/use-toast';

const DEFAULT_DURATION_MS = 5000;

interface ToastTimer {
  handle: ReturnType<typeof setTimeout> | null;
  remainingMs: number;
  startedAt: number;
}

function startTimer(timer: ToastTimer, onExpire: () => void) {
  timer.startedAt = Date.now();
  timer.handle = setTimeout(onExpire, timer.remainingMs);
}

function pauseTimer(timer: ToastTimer) {
  if (timer.handle === null) {
    return;
  }
  clearTimeout(timer.handle);
  timer.handle = null;
  timer.remainingMs = Math.max(0, timer.remainingMs - (Date.now() - timer.startedAt));
}

/**
 * Auto-dismiss timers for the visible toasts, pausable as a set and holdable
 * individually.
 *
 * React Aria's `useToastRegion` calls `pauseAll`/`resumeAll` whenever the region
 * is hovered or holds focus, so a toast cannot expire out from under someone
 * reading it or tabbing to its action. A held toast outlives that: hover ends
 * the moment the pointer leaves, but an expanded message is still being read,
 * so it stays until the reader collapses or dismisses it.
 */
export function useToastTimers(toasts: ToasterToast[]) {
  const timersRef = React.useRef(new Map<string, ToastTimer>());
  const heldRef = React.useRef(new Set<string>());
  const isPausedRef = React.useRef(false);

  React.useEffect(() => {
    const timers = timersRef.current;
    const live = new Set(toasts.filter((toast) => toast.open !== false).map((toast) => toast.id));

    for (const [id, timer] of timers) {
      if (!live.has(id)) {
        pauseTimer(timer);
        timers.delete(id);
      }
    }

    for (const toast of toasts) {
      if (toast.open === false || timers.has(toast.id)) {
        continue;
      }
      const timer: ToastTimer = {
        handle: null,
        remainingMs: toast.duration ?? DEFAULT_DURATION_MS,
        startedAt: Date.now(),
      };
      timers.set(toast.id, timer);
      if (!isPausedRef.current && !heldRef.current.has(toast.id)) {
        startTimer(timer, () => dismissToast(toast.id));
      }
    }

    for (const id of heldRef.current) {
      if (!live.has(id)) {
        heldRef.current.delete(id);
      }
    }
  }, [toasts]);

  React.useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        pauseTimer(timer);
      }
      timers.clear();
    };
  }, []);

  const pauseAll = React.useCallback(() => {
    isPausedRef.current = true;
    for (const timer of timersRef.current.values()) {
      pauseTimer(timer);
    }
  }, []);

  const resumeAll = React.useCallback(() => {
    isPausedRef.current = false;
    for (const [id, timer] of timersRef.current) {
      if (timer.handle === null && !heldRef.current.has(id)) {
        startTimer(timer, () => dismissToast(id));
      }
    }
  }, []);

  const hold = React.useCallback((id: string) => {
    heldRef.current.add(id);
    const timer = timersRef.current.get(id);
    if (timer) {
      pauseTimer(timer);
    }
  }, []);

  const release = React.useCallback((id: string) => {
    heldRef.current.delete(id);
    const timer = timersRef.current.get(id);
    if (timer?.handle === null && !isPausedRef.current) {
      startTimer(timer, () => dismissToast(id));
    }
  }, []);

  return { pauseAll, resumeAll, hold, release };
}
